<template>
  <div
    ref="pageEl"
    class="relative shrink-0 bg-white"
    :style="{ width: `${width}px`, height: `${height}px` }"
    :aria-busy="!rendered"
    :data-certificate-page="pageNumber"
  >
    <canvas
      ref="canvasEl"
      role="img"
      :aria-label="`Certificate, page ${pageNumber} of ${document.numPages}`"
      class="block size-full"
    />
    <span class="sr-only">{{ text }}</span>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  watch,
} from 'vue'
import type { PDFDocumentProxy, PDFPageProxy, RenderTask } from 'pdfjs-dist'
import type { TextContent } from 'pdfjs-dist/types/src/display/api'

const props = defineProps<{
  document: PDFDocumentProxy
  pageNumber: number
  availableWidth: number
  availableHeight: number
  zoom: number
  renderScale?: number
}>()
const emit = defineEmits<{ error: []; rendered: [] }>()
const pageEl = ref<HTMLElement>()
const canvasEl = ref<HTMLCanvasElement>()
const page = shallowRef<PDFPageProxy>()
const visible = ref(false)
const rendered = ref(false)
const text = ref('')
const ratio = computed(() => {
  const viewport = page.value?.getViewport({ scale: 1 })
  return viewport ? viewport.width / viewport.height : 612 / 792
})
const width = computed(
  () =>
    Math.max(
      1,
      Math.min(props.availableWidth, props.availableHeight * ratio.value)
    ) * props.zoom
)
const height = computed(() => width.value / ratio.value)
let observer: IntersectionObserver | undefined
let task: RenderTask | undefined
let revision = 0
let disposed = false

async function renderPage() {
  const current = ++revision
  const previous = task
  previous?.cancel()
  if (previous) await previous.promise.catch(() => {})
  if (
    disposed ||
    current !== revision ||
    !visible.value ||
    !page.value ||
    !canvasEl.value
  )
    return
  const canvas = canvasEl.value
  const original = page.value.getViewport({ scale: 1 })
  const scale = Math.min(
    Math.min(window.devicePixelRatio || 1, 3) * (props.renderScale ?? 1),
    Math.sqrt(16_000_000 / (width.value * height.value))
  )
  const viewport = page.value.getViewport({
    scale: (width.value / original.width) * scale,
  })
  canvas.width = Math.ceil(viewport.width)
  canvas.height = Math.ceil(viewport.height)
  rendered.value = false
  try {
    const next = page.value.render({ canvas, viewport })
    task = next
    await next.promise
    if (disposed || current !== revision) return
    rendered.value = true
    emit('rendered')
  } catch (error) {
    if (
      !disposed &&
      current === revision &&
      !(error instanceof Error && error.name === 'RenderingCancelledException')
    )
      emit('error')
  }
}

watch([width, height, visible, page, () => props.renderScale], renderPage, {
  flush: 'post',
})

onMounted(async () => {
  if (pageEl.value) {
    observer = new IntersectionObserver(
      ([entry]) => {
        visible.value = entry.isIntersecting
      },
      {
        root: pageEl.value.closest('[data-certificate-pane]'),
        rootMargin: '300px',
      }
    )
    observer.observe(pageEl.value)
  }
  try {
    const loaded = await props.document.getPage(props.pageNumber)
    if (disposed) return
    page.value = loaded
    const reader: ReadableStreamDefaultReader<TextContent> = loaded
      .streamTextContent()
      .getReader()
    const parts: string[] = []
    try {
      while (!disposed) {
        const { value, done } = await reader.read()
        if (done) break
        parts.push(
          ...value.items.map((item) => ('str' in item ? item.str : ''))
        )
      }
      if (!disposed) text.value = parts.join(' ')
    } finally {
      reader.releaseLock()
    }
  } catch {
    if (!disposed) emit('error')
  }
})

onBeforeUnmount(() => {
  disposed = true
  revision++
  observer?.disconnect()
  task?.cancel()
})
</script>
